import React, { useState } from 'react';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import styled from "styled-components";
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { Moment } from "moment";
import { DateRangePicker } from '@shared/ui/molecules/DateRangePicker';
import { warningNotification } from "@shared/store/notification.duck";
import { getWhereaboutsReport, toExcelDownload } from "@features/reporting/services/ReportingService";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { RadioButton } from '@shared/ui/atoms/RadioButton';

export enum WhereaboutsReportType {
  HighLevel = 'HIGH_LEVEL',
  Detailed = 'DETAILED',
}

export function DownloadReportSection(props: Props) {
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<Moment | undefined>();
  const [toDate, setToDate] = useState<Moment | undefined>();
  const [reportType, setReportType] = useState(WhereaboutsReportType.HighLevel);
  const {t} = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const dispatch = useDispatch();

  const downloadDetailedReport = (data: any[]) => {
    const reportData = data.map((d: any) => ({
      date: d.date ?? '',
      firstName: d.firstName ?? '',
      lastName: d.lastName ?? '',
      email: d.email ?? '',
      team: d.team ?? '',
      department: d.department ?? '',
      amStatus: d.amStatus ?? '',
      amOffice: d.amOffice ?? '',
      amFloor: d.amFloor ?? '',
      amArea: d.amArea ?? '',
      pmStatus: d.pmStatus ?? '',
      pmOffice: d.pmOffice ?? '',
      pmFloor: d.pmFloor ?? '',
      pmArea: d.pmArea ?? '',
    }));
    toExcelDownload(reportData, 'Detailed whereabouts report');
  }

  const downloadHighLevelReport = (data: any[]) => {
    const reportData: any = [];
    data.forEach((d: any) => {
      const amLabel = d.amOffice || d.amStatus;
      const am = reportData.find((rd: any) => rd.label === amLabel);
      if (am) am.count += 0.5;
      else reportData.push({label: amLabel, count: 0.5});

      const pmLabel = d.pmOffice || d.pmStatus;
      const pm = reportData.find((rd: any) => rd.label === pmLabel);
      if (pm) pm.count += 0.5;
      else reportData.push({label: pmLabel, count: 0.5});

      if (d.amOffice) {
        const am = reportData.find((rd: any) => rd.label === 'Office (Total)');
        if (am) am.count += 0.5;
        else reportData.push({label: 'Office (Total)', count: 0.5});
      }

      if (d.pmOffice) {
        const pm = reportData.find((rd: any) => rd.label === 'Office (Total)');
        if (pm) pm.count += 0.5;
        else reportData.push({label: 'Office (Total)', count: 0.5});
      }

    });
    toExcelDownload(reportData, 'Whereabouts report');
  }

  const downloadReport = async () => {
    if (currentUser && props.selectedUserId) {
      if (fromDate && toDate) {
        setLoading(true);
        try {
          const data = await getWhereaboutsReport(currentUser.companyEntity.id, fromDate, toDate, props.selectedUserId)
          if (reportType === WhereaboutsReportType.Detailed) {
            downloadDetailedReport(data);
          } else {
            downloadHighLevelReport(data);
          }
        } finally {
          setLoading(false);
        }
      } else {
        dispatch(warningNotification('Set to and from dates'));
      }
    }
    setLoading(false);
  }

  const onDateSelected = (startDate?: Moment, endDate?: Moment) => {
    setFromDate(startDate);
    setToDate(endDate);
  }

  return (
    <>
      <DownloadReportContainer>
        <HeadlineSmall style={{marginBottom: 8}}>{t('reports-page.holidays-report.report')}</HeadlineSmall>
        <ReportTypeSelector>
          <RadioButton value={'highLevelReport'}
                       selected={reportType === WhereaboutsReportType.HighLevel}
                       onChange={() => setReportType(WhereaboutsReportType.HighLevel)}
                       text={'reports-page.whereabouts-report.high-level-report'} />
          <RadioButton value={'detailedReport'}
                       selected={reportType === WhereaboutsReportType.Detailed}
                       onChange={() => setReportType(WhereaboutsReportType.Detailed)}
                       text={'reports-page.whereabouts-report.detailed-report'} />
        </ReportTypeSelector>
        <Content>
          <DateRangePicker disabled={loading} onDatesSelected={onDateSelected} toDate={toDate} fromDate={fromDate} />
          <OutlineButton click={downloadReport}
                         text={'button.download-csv'}
                         disabled={loading && !fromDate && !toDate && !props.selectedUserId} />
        </Content>
      </DownloadReportContainer>
    </>
  )
}

interface Props {
  selectedUserId?: string;
}


const DownloadReportContainer = styled.div`
  margin-top: 60px;
`

const ReportTypeSelector = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  @media (${tablet}) {
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  }
`
